/**
 * Nobl9NfrProjectFieldExtension.tsx
 * ──────────────────────────────────
 * A Backstage scaffolder field extension that renders a searchable autocomplete
 * dropdown listing ONLY Nobl9 projects tagged with `nfr: True`.
 *
 * Uses the same `useNobl9Metadata()` hook as the base Nobl9ProjectFieldExtension
 * but filters the project list to only NFR-eligible projects.
 *
 * Sets window.__nobl9SelectedProjectName and window.__nobl9SelectedProjectId
 * so the downstream Nobl9_ServiceSelect extension can filter services correctly.
 *
 * Field extension name: Nobl9_NfrProjectSelect
 */

import { useEffect, useState, useMemo } from 'react';
import {
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { FormControl, TextField, FormHelperText } from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { renderLoadingAdornment } from './fieldUtils';
import { useNobl9Metadata } from '@internal/plugin-nobl9-metadata-frontend';

export const Nobl9NfrProjectFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

interface Props extends FieldExtensionComponentProps<string> {}

export const Nobl9NfrProjectFieldComponent = ({
  onChange,
  formData,
  rawErrors,
  schema,
}: Props) => {
  const { projects, state, error } = useNobl9Metadata();
  const [value, setValue] = useState(formData || '');

  // Propagate slug value only when valid
  useEffect(() => {
    if (value) {
      const slugOk = /^[a-z0-9]+[a-z0-9-]*[a-z0-9]+$/.test(value);
      if (slugOk) onChange(value);
    }
  }, [value, onChange]);

  const loading = state === 'loading' || state === 'refreshing';

  // Filter to projects where the nfr flag is set
  const nfrProjects = useMemo(() => {
    const filtered = projects.filter(p => p.nfr === true);
    return filtered
      .map(p => ({
        name: p.name,
        displayName: p.displayName || p.name,
        bu: (p as any).bu || 'Other',
        appInventoryId: (p as any).appInventoryId,
        label: (p as any).appInventoryId
          ? `${p.displayName || p.name} (${(p as any).appInventoryId})`
          : p.displayName || p.name,
      }))
      .sort((a, b) => {
        const buCmp = a.bu.localeCompare(b.bu);
        if (buCmp !== 0) return buCmp;
        return a.displayName.localeCompare(b.displayName);
      });
  }, [projects]);

  const rawDescription: string = (schema as any)?.description || '';
  const statusLine = useMemo(() => {
    if (loading) return 'Loading NFR projects…';
    if (error) return 'Error loading projects';
    if (!loading && projects.length > 0 && nfrProjects.length === 0)
      return 'No NFR projects found. Ask the SRE team to set nfr: True on your project.';
    return '';
  }, [loading, error, projects.length, nfrProjects.length]);

  return (
    <FormControl
      fullWidth
      margin="dense"
      error={!!rawErrors?.length || !!error}
    >
      <Autocomplete
        disabled={loading}
        options={nfrProjects}
        groupBy={opt => opt.bu}
        getOptionLabel={opt => opt.label}
        value={value ? nfrProjects.find(p => p.name === value) || null : null}
        onChange={(_e, newOpt) => {
          if (!newOpt) {
            setValue('');
            (window as any).__nobl9SelectedProjectName = undefined;
            (window as any).__nobl9SelectedProjectId = undefined;
            (window as any).__nobl9SelectedProjectInternalId = undefined;
            return;
          }
          setValue(newOpt.name);
          // These globals are consumed by Nobl9_ServiceSelect to filter services
          (window as any).__nobl9SelectedProjectName = newOpt.name;
          (window as any).__nobl9SelectedProjectId = newOpt.name;
          (window as any).__nobl9SelectedProjectInternalId =
            newOpt.appInventoryId;
        }}
        loading={loading}
        renderInput={params => (
          <TextField
            {...params}
            label="Nobl9 NFR Project"
            placeholder="Search NFR projects…"
            InputProps={{
              ...params.InputProps,
              endAdornment: renderLoadingAdornment(params, loading),
            }}
          />
        )}
        filterSelectedOptions
        autoHighlight
        clearOnBlur={false}
        openOnFocus
        noOptionsText={
          loading
            ? 'Loading…'
            : 'No matching NFR projects. Projects must have nfr: True label in Nobl9.'
        }
        isOptionEqualToValue={(opt, val) => opt.name === (val?.name || val)}
      />
      {(rawDescription || statusLine) && (
        <FormHelperText>{rawDescription || statusLine}</FormHelperText>
      )}
    </FormControl>
  );
};

export const Nobl9NfrProjectFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_NfrProjectSelect',
    component: Nobl9NfrProjectFieldComponent,
    schema: Nobl9NfrProjectFieldSchema,
  }),
);

export default Nobl9NfrProjectFieldExtension;
